// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Application chrome for the University Control Center.
 *
 * Accessibility obligations from `docs/10-TESTING.md` §30 and
 * `docs/05-CODING-STANDARDS.md` §26 are met structurally, not decoratively:
 *
 * - a skip link is the first focusable element so keyboard users can bypass
 *   navigation;
 * - navigation is a labelled `nav` landmark of real links, and the current
 *   item carries `aria-current="page"`;
 * - the routed area is a labelled `main` landmark with a focus target, so a
 *   route change moves focus rather than stranding it; and
 * - only areas the verified session was offered are listed, which is a
 *   usability affordance and never an authorization decision (`CST-048`).
 *
 * The chrome also states the tenant scope and, when the BFF reports it, the
 * degraded serving mode — `ADM-014` forbids presenting a degraded platform as
 * healthy.
 */

import { hasCapability, useSession } from '@eai/ui-shared';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import { useEffect, useRef, type ReactNode } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { navigationItems } from './navigation';

/** Element id of the routed region, referenced by the skip link. */
export const MAIN_CONTENT_ID = 'control-plane-main';

/** Props for {@link AppLayout}. */
export interface AppLayoutProps {
  readonly children: ReactNode;
}

/** Render the application chrome around a routed screen. */
export function AppLayout({ children }: AppLayoutProps): ReactNode {
  const { session, signOut } = useSession();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Move focus to the routed region on navigation so keyboard and
    // screen-reader users are not left at the previous screen's position.
    mainRef.current?.focus();
  }, [location.pathname]);

  const visibleItems = navigationItems.filter((item) =>
    hasCapability(session, item.requiredCapability),
  );

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Link
        href={`#${MAIN_CONTENT_ID}`}
        sx={{
          position: 'absolute',
          left: 8,
          top: 8,
          zIndex: 1400,
          p: 1,
          bgcolor: 'background.paper',
          '&:not(:focus):not(:active)': visuallyHidden,
        }}
      >
        Skip to main content
      </Link>

      <AppBar position="static" component="header" color="default" elevation={0}>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h3" component="p" sx={{ flexGrow: 1 }}>
            University Control Center
          </Typography>
          <Stack alignItems="flex-end">
            <Typography variant="body2">
              {session?.university_label ?? 'Scope not resolved'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {session?.profile?.display_name ?? session?.subject_id ?? 'Not signed in'}
            </Typography>
          </Stack>
          <Button
            onClick={() => {
              signOut().catch(() => undefined);
            }}
          >
            Sign out
          </Button>
        </Toolbar>
        {session?.degraded === true ? (
          <Box sx={{ px: 2, pb: 1 }} role="status">
            <Typography variant="body2">
              The platform is running in a degraded serving mode
              {session.serving_mode === null ? '' : ` (${session.serving_mode})`}. Some information
              may be unavailable.
            </Typography>
          </Box>
        ) : null}
      </AppBar>

      <Box sx={{ display: 'flex', flex: 1, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box
          component="nav"
          aria-label="Control plane sections"
          sx={{ minWidth: { md: 260 }, borderRight: { md: 1 }, borderColor: 'divider' }}
        >
          <List>
            {visibleItems.map((item) => {
              const current = location.pathname === item.path;
              return (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    selected={current}
                    {...(current ? { 'aria-current': 'page' as const } : {})}
                  >
                    <ListItemText primary={item.label} secondary={item.description} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
          {visibleItems.length === 0 ? (
            <Typography variant="body2" sx={{ p: 2 }}>
              No administration area is available for your account.
            </Typography>
          ) : null}
        </Box>

        <Container
          component="main"
          id={MAIN_CONTENT_ID}
          ref={mainRef}
          tabIndex={-1}
          aria-label="Control plane content"
          sx={{ py: 3, outline: 'none' }}
          maxWidth={false}
        >
          {children}
        </Container>
      </Box>
    </Box>
  );
}
